import { AuthClient } from "@dfinity/auth-client";
import type { Identity } from "@dfinity/agent";

const II_URL_PROD = "https://id.ai";
const SESSION_HOURS = 168;

const DERIVATION_ORIGIN_PROD = "https://2npqq-myaaa-aaaao-ba6tq-cai.icp0.io";

function isLocalHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

function getIdentityProviderUrl(): string {
  if (typeof window === "undefined") return II_URL_PROD;
  if (!isLocalHost(window.location.hostname)) return II_URL_PROD;
  const port = window.location.port || "4943";
  return `http://id.ai.localhost:${port}`;
}

function getDerivationOrigin(): string | undefined {
  if (typeof window === "undefined") return DERIVATION_ORIGIN_PROD;
  if (!isLocalHost(window.location.hostname)) return DERIVATION_ORIGIN_PROD;
  return undefined;
}

let _client: AuthClient | null = null;

async function getClient(): Promise<AuthClient> {
  if (!_client) {
    _client = await AuthClient.create({ idleOptions: { disableIdle: true } });
  }
  return _client;
}

export type AuthState = {
  identity: Identity | null;
  isAuthenticated: boolean;
  principalText: string;
};

export async function initAuth(): Promise<AuthState> {
  return refreshAuthState();
}

export async function refreshAuthState(): Promise<AuthState> {
  const client = await getClient();
  let authenticated: boolean;
  try {
    authenticated = await client.isAuthenticated();
  } catch {
    return { identity: null, isAuthenticated: false, principalText: "" };
  }
  if (authenticated) {
    const identity = client.getIdentity();
    return {
      identity,
      isAuthenticated: true,
      principalText: identity.getPrincipal().toText(),
    };
  }
  return { identity: null, isAuthenticated: false, principalText: "" };
}

export async function login(): Promise<AuthState> {
  const client = await getClient();
  await new Promise<void>((resolve, reject) => {
    client.login({
      identityProvider: getIdentityProviderUrl(),
      derivationOrigin: getDerivationOrigin(),
      maxTimeToLive: BigInt(SESSION_HOURS) * BigInt(3_600_000_000_000),
      onSuccess: resolve,
      onError: reject,
    });
  });
  const identity = client.getIdentity();
  return {
    identity,
    isAuthenticated: true,
    principalText: identity.getPrincipal().toText(),
  };
}

export async function logout(): Promise<void> {
  const client = await getClient();
  await client.logout();
  _client = null;
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("ic-identity");
    window.localStorage.removeItem("ic-delegation");
    window.localStorage.removeItem("ic-iv");
  }
}
